// Copyright 2025 Sorama B.V.
import { AnalysisWorkflowModuleCatalog } from "catalogs/WorkflowModuleCatalog/AnalysisWorkflowModuleCatalog";
import { AxisKind, LinearFrequencyAxisKind, LogarithmicFrequencyAxisKind } from "datatypes/SettingsKinds/AxisKinds";
import { IWorkflowModuleConfig } from "libraries/WorkflowFramework/Interfaces/IWorkflowModuleConfig";
import { WorkflowProperty } from "libraries/WorkflowFramework/WorkflowProperties/WorkflowPropertyObserverManager";
import {
    HarmonicSelectionModes,
    NarrowbandSelectionModes,
} from "modules/workflows/Analysis/Spectrum/SpectrumModuleModes";
import { Pressure } from "units/Pressure";

export interface AnalysisPreset {
    name: string;
    type: AnalysisPresetType;
    title: string;
    icon?: string;
    analysisModulePresets: AnalysisModulePreset[];
}

export type AnalysisPresetType = "leak" | "partialDischarge" | "mechanical";

export interface AnalysisModulePreset {
    moduleConfig: IWorkflowModuleConfig;
    settingsName: string;
    defaultProperties?: WorkflowProperty[];
    defaultSettings?: UserSettingPreset[];
}

export interface UserSettingPreset {
    name: string;
    value: unknown;
    valueType?: string;
}

export class AnalysisPresets {
    // Analysis module presets are very similar; constructed based on the few variable properties and/or settings
    private static getAnalysisModulePresets(
        narrowBandSelectionMode: string,
        harmonicSelectionMode: string,
        selectedFrequencyAxisKind: AxisKind
    ): AnalysisModulePreset[] {
        return [
            {
                moduleConfig: AnalysisWorkflowModuleCatalog.SpectrumModule,
                settingsName: "SpectrumModule",
                defaultProperties: [
                    // Variable properties
                    {
                        name: "narrowBandSelectionMode",
                        value: narrowBandSelectionMode,
                    },
                    {
                        name: "harmonicSelectionMode",
                        value: harmonicSelectionMode,
                    },
                    // Default properties
                    {
                        name: "showLegend",
                        value: false,
                    },
                ],
                defaultSettings: [
                    // Variable settings
                    {
                        name: "selectedFrequencyAxisKind",
                        value: selectedFrequencyAxisKind,
                        valueType: "AxisKind",
                    },
                    // Default settings
                    {
                        name: "selectedUnit",
                        value: Pressure.dbSPL,
                        valueType: "WeightedUnit",
                    },
                ],
            },
            {
                moduleConfig: AnalysisWorkflowModuleCatalog.FarfieldModule,
                settingsName: "FarfieldModule",
                defaultProperties: [],
                defaultSettings: [
                    {
                        name: "probes",
                        value: false,
                        valueType: "Boolean",
                    },
                    {
                        name: "ticksFarfield",
                        value: false,
                        valueType: "Boolean",
                    },
                    {
                        name: "showLegendFarfield",
                        value: true,
                        valueType: "Boolean",
                    },
                    {
                        name: "legendPosition",
                        value: true,
                        valueType: "Boolean",
                    },
                    {
                        name: "fps",
                        value: 25,
                        valueType: "Number",
                    },
                    {
                        name: "opacity",
                        value: 60,
                        valueType: "Number",
                    },
                    {
                        name: "threshold",
                        value: 30,
                        valueType: "Number",
                    },
                    {
                        name: "videoOffsetMs",
                        value: 0,
                        valueType: "Number",
                    },
                ],
            },
        ];
    }

    public static BearingModulePresets: AnalysisModulePreset[] = this.getAnalysisModulePresets(
        NarrowbandSelectionModes.maxBand,
        HarmonicSelectionModes.none,
        LogarithmicFrequencyAxisKind
    );
    public static PumpsModulePresets: AnalysisModulePreset[] = this.getAnalysisModulePresets(
        NarrowbandSelectionModes.none,
        HarmonicSelectionModes.none,
        LinearFrequencyAxisKind
    );
    public static LeaksModulePresets: AnalysisModulePreset[] = this.getAnalysisModulePresets(
        NarrowbandSelectionModes.none,
        HarmonicSelectionModes.none,
        LinearFrequencyAxisKind
    );
    public static PartialDischargeModulePresets: AnalysisModulePreset[] = this.getAnalysisModulePresets(
        NarrowbandSelectionModes.none,
        HarmonicSelectionModes.none,
        LinearFrequencyAxisKind
    );
}
